/**
 * Reset Password Page
 *
 * Allows users to set a new password after clicking the reset link from email.
 * Supabase automatically handles the token validation when user lands on this page.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);

  // Check if user has a valid recovery session
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      // User should have a session if they clicked the reset link
      // Supabase automatically logs them in with a recovery session
      if (session) {
        setIsValidSession(true);
      } else {
        setIsValidSession(false);
      }
    };

    checkSession();

    // Listen for auth state changes (Supabase fires PASSWORD_RECOVERY event)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, _session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const validatePassword = (): string | null => {
    if (password.length < 8) {
      return t('auth.passwordMinLength');
    }
    if (password !== confirmPassword) {
      return t('auth.passwordsNoMatch');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      // Mark password as set in profiles
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ password_set: true })
          .eq('id', user.id);
      }

      setIsSuccess(true);

      // Sign out after password reset so user can log in fresh
      await supabase.auth.signOut();
    } catch (err: any) {
      setError(err.message || t('auth.failedResetPassword'));
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="space-y-xl">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-lg">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.resetSuccess')}</h2>
          <p className="text-secondary text-sm">
            {t('auth.resetSuccessDesc')}
          </p>
        </div>

        <button
          onClick={() => navigate('/login')}
          className="btn btn-primary w-full"
        >
          {t('auth.signIn')}
        </button>
      </div>
    );
  }

  // Invalid/expired session state
  if (isValidSession === false) {
    return (
      <div className="space-y-xl">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-warning/10 mb-lg">
            <AlertCircle className="w-8 h-8 text-warning" />
          </div>
          <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.invalidLink')}</h2>
          <p className="text-secondary text-sm">
            {t('auth.invalidLinkDesc')}
          </p>
        </div>

        <div className="space-y-md">
          <button
            onClick={() => navigate('/forgot-password')}
            className="btn btn-primary w-full"
          >
            {t('auth.requestNewLink')}
          </button>
          <button
            onClick={() => navigate('/login')}
            className="btn btn-secondary w-full"
          >
            {t('auth.backToSignIn')}
          </button>
        </div>
      </div>
    );
  }

  // Loading state while checking session
  if (isValidSession === null) {
    return (
      <div className="flex items-center justify-center py-xl">
        <div className="w-8 h-8 border-2 border-blue/30 border-t-blue rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-xl">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-primary mb-sm">{t('auth.resetYourPassword')}</h2>
        <p className="text-secondary text-sm">
          {t('auth.enterNewPassword')}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-md flex items-start gap-md">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-800 font-medium">{t('common.error')}</p>
            <p className="text-sm text-red-600 mt-xs">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-lg">
        {/* New Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-sm text-primary">
            {t('auth.newPassword')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.enterNewPasswordPlaceholder')}
              className="input pl-11 pr-11"
              required
              minLength={8}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-secondary mt-xs">{t('auth.minChars')}</p>
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-sm text-primary">
            {t('auth.confirmNewPassword')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('auth.confirmNewPasswordPlaceholder')}
              className="input pl-11 pr-11"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t('auth.resetting')}
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              {t('auth.resetPassword')}
            </>
          )}
        </button>
      </form>

      {/* Back to Login */}
      <div className="pt-lg border-t border-border">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="flex items-center justify-center gap-sm text-sm text-secondary hover:text-primary transition-colors w-full"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.backToSignIn')}
        </button>
      </div>
    </div>
  );
}
